import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import type { TeamMembership } from '../../../lib/api';
import { useAuthSessionStore } from '../../auth/session-store';
import { useOnlineStatus } from '../hooks/use-online-status';
import { usePromptSource, type UnifiedFolder, type UnifiedPrompt } from '../hooks/use-prompt-source';
import { parseTemplateVariables, type TemplateVariable } from '../lib/template';
import { useCloudPromptsStore } from '../store/cloud-prompts';
import { useTeamStore, type ActiveWorkspace } from '../store/teams';
import ConfirmDialog from '../../../components/shared/ConfirmDialog';
import JoinTeamDialog from './JoinTeamDialog';
import PromptEditorDialog from './PromptEditorDialog';
import PromptFolderSection from './PromptFolderSection';
import PromptsToolbar from './PromptsToolbar';
import TemplateFillDialog from './TemplateFillDialog';
import TextInputDialog from './TextInputDialog';
import type { FolderNameDialogState, PromptEditorState, TemplateFillState } from './prompts-view-types';
import WorkspaceSelector from './WorkspaceSelector';

const MAX_TEAM_NAME_LENGTH = 50;

interface PromptsViewProps {
  onInjectPrompt: (text: string) => Promise<{ success: boolean; error?: string }>;
}

function isTeamMembership(value: unknown): value is TeamMembership {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.teamId === 'string' &&
    typeof candidate.teamName === 'string' &&
    (candidate.role === 'owner' || candidate.role === 'member') &&
    typeof candidate.joinedAt === 'string'
  );
}

function getTeamsFromUser(user: Record<string, unknown> | null): TeamMembership[] {
  if (!user || !Array.isArray(user.teams)) {
    return [];
  }

  return user.teams.filter(isTeamMembership);
}

function buildInitialTemplateValues(variables: TemplateVariable[]): Record<string, string> {
  return variables.reduce<Record<string, string>>((accumulator, variable) => {
    accumulator[variable.name] = variable.defaultValue ?? '';
    return accumulator;
  }, {});
}

function createPromptEditorState(prompt?: UnifiedPrompt): PromptEditorState {
  if (!prompt) {
    return {
      mode: 'create',
      prompt: null,
      title: '',
      content: '',
      folderId: null,
    };
  }

  return {
    mode: 'edit',
    prompt,
    title: prompt.title,
    content: prompt.content,
    folderId: prompt.folderId,
  };
}

export default function PromptsView({ onInjectPrompt }: Readonly<PromptsViewProps>): JSX.Element {
  const {
    prompts,
    folders,
    searchFilter,
    isLoading,
    error,
    isCloudSource,
    setSearchFilter,
    addPrompt,
    updatePrompt,
    deletePrompt,
    addFolder,
    updateFolder,
    deleteFolder,
    loadPrompts,
    loadFolders,
    clearError,
  } = usePromptSource();

  const user = useAuthSessionStore((state) => state.user);
  const teams = useMemo(() => getTeamsFromUser(user), [user]);

  const activeWorkspace = useTeamStore((state) => state.activeWorkspace);
  const setActiveWorkspace = useTeamStore((state) => state.setActiveWorkspace);
  const createTeam = useTeamStore((state) => state.createTeam);
  const lookupTeamInvite = useTeamStore((state) => state.lookupTeamInvite);
  const joinTeam = useTeamStore((state) => state.joinTeam);
  const isCreatingTeam = useTeamStore((state) => state.isCreatingTeam);
  const isLookingUpTeamInvite = useTeamStore((state) => state.isLookingUpTeamInvite);
  const isJoiningTeam = useTeamStore((state) => state.isJoiningTeam);
  const createTeamError = useTeamStore((state) => state.createTeamError);
  const joinTeamError = useTeamStore((state) => state.joinTeamError);
  const clearCreateTeamError = useTeamStore((state) => state.clearCreateTeamError);
  const clearJoinTeamError = useTeamStore((state) => state.clearJoinTeamError);
  const refreshForWorkspace = useCloudPromptsStore((state) => state.refreshForWorkspace);

  const { isOffline } = useOnlineStatus();

  const [editorState, setEditorState] = useState<PromptEditorState | null>(null);
  const [templateFillState, setTemplateFillState] = useState<TemplateFillState | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [folderNameDialog, setFolderNameDialog] = useState<FolderNameDialogState | null>(null);
  const [isCreateTeamDialogOpen, setIsCreateTeamDialogOpen] = useState(false);
  const [isJoinTeamDialogOpen, setIsJoinTeamDialogOpen] = useState(false);
  const [promptPendingDelete, setPromptPendingDelete] = useState<UnifiedPrompt | null>(null);
  const [folderPendingDelete, setFolderPendingDelete] = useState<UnifiedFolder | null>(null);

  const filteredPrompts = useMemo(() => {
    if (!searchFilter.trim()) {
      return prompts;
    }

    const needle = searchFilter.toLowerCase();
    return prompts.filter((prompt) =>
      prompt.title.toLowerCase().includes(needle) ||
      prompt.content.toLowerCase().includes(needle),
    );
  }, [prompts, searchFilter]);

  const sortedFolders = useMemo(
    () => [...folders].sort((a, b) => a.name.localeCompare(b.name)),
    [folders],
  );

  const promptsByFolder = useMemo(() => {
    const grouped = new Map<string | null, UnifiedPrompt[]>();

    for (const folder of sortedFolders) {
      grouped.set(folder.id, []);
    }

    grouped.set(null, []);

    const sortedPrompts = [...filteredPrompts].sort((a, b) => b.updatedAt - a.updatedAt);
    for (const prompt of sortedPrompts) {
      const bucket = grouped.get(prompt.folderId) ?? [];
      bucket.push(prompt);
      grouped.set(prompt.folderId, bucket);
    }

    return grouped;
  }, [filteredPrompts, sortedFolders]);

  const handleWorkspaceSelect = async (workspace: ActiveWorkspace) => {
    const isSame =
      activeWorkspace.type === workspace.type &&
      (workspace.type === 'personal' ||
        (activeWorkspace.type === 'team' && activeWorkspace.teamId === workspace.teamId));

    if (isSame) {
      return;
    }

    setActiveWorkspace(workspace);
    await refreshForWorkspace();
  };

  const handleWorkspaceChange = (value: string) => {
    if (value === 'personal') {
      void handleWorkspaceSelect({ type: 'personal' });
      return;
    }

    const teamId = value.replace('team:', '');
    const team = teams.find((candidate) => candidate.teamId === teamId);
    if (!team) {
      return;
    }

    void handleWorkspaceSelect({
      type: 'team',
      teamId: team.teamId,
      teamName: team.teamName,
    });
  };

  const handleSavePrompt = async (next: PromptEditorState) => {
    if (next.mode === 'create') {
      await addPrompt(next.title.trim(), next.content.trim(), next.folderId);
    } else if (next.prompt) {
      await updatePrompt(next.prompt.id, next.title.trim(), next.content.trim(), next.folderId);
    }

    setEditorState(null);
  };

  const handleInsertPrompt = async (prompt: UnifiedPrompt) => {
    const variables = parseTemplateVariables(prompt.content);
    if (variables.length > 0) {
      setTemplateFillState({
        prompt,
        variables,
        values: buildInitialTemplateValues(variables),
      });
      return;
    }

    const result = await onInjectPrompt(prompt.content);
    setStatusMessage(result.success ? 'Prompt inserted.' : result.error ?? 'Failed to insert prompt.');
  };

  const handleResolvedInsert = async (resolvedPrompt: string) => {
    const result = await onInjectPrompt(resolvedPrompt);
    setTemplateFillState(null);
    setStatusMessage(result.success ? 'Prompt inserted.' : result.error ?? 'Failed to insert prompt.');
  };

  const handleConfirmPromptDelete = async () => {
    if (!promptPendingDelete) {
      return;
    }

    await deletePrompt(promptPendingDelete.id);
    setPromptPendingDelete(null);
  };

  const handleConfirmFolderDelete = async () => {
    if (!folderPendingDelete) {
      return;
    }

    await deleteFolder(folderPendingDelete.id);
    setFolderPendingDelete(null);
  };

  const handleConfirmFolderName = async (folderName: string) => {
    if (!folderNameDialog) {
      return;
    }

    if (folderNameDialog.mode === 'create') {
      await addFolder(folderName);
    } else {
      await updateFolder(folderNameDialog.folder.id, folderName);
    }

    setFolderNameDialog(null);
  };

  const handleCreateTeam = async (teamName: string) => {
    const workspace = await createTeam(teamName);
    if (!workspace) {
      return;
    }

    setIsCreateTeamDialogOpen(false);
    setActiveWorkspace(workspace);
    await refreshForWorkspace();
  };

  const handleJoinTeam = async (inviteCode: string) => {
    const workspace = await joinTeam(inviteCode);
    if (!workspace) {
      return false;
    }

    setIsJoinTeamDialogOpen(false);
    setActiveWorkspace(workspace);
    await refreshForWorkspace();
    return true;
  };

  const handleRetry = () => {
    clearError();
    void loadPrompts();
    void loadFolders();
  };

  const unfiledPrompts = promptsByFolder.get(null) ?? [];

  const pendingFolderPromptCount = folderPendingDelete
    ? prompts.filter((prompt) => prompt.folderId === folderPendingDelete.id).length
    : 0;

  const folderDeleteDescription = folderPendingDelete
    ? `Delete folder "${folderPendingDelete.name}"${pendingFolderPromptCount > 0 ? ` and its ${pendingFolderPromptCount} prompt${pendingFolderPromptCount === 1 ? '' : 's'}` : ''}?`
    : '';

  return (
    <div className="relative flex h-full flex-col">
      <div className="flex flex-col gap-3 overflow-y-auto p-3 pb-20">
      {isCloudSource ? (
        <WorkspaceSelector
          activeWorkspace={activeWorkspace}
          teams={teams}
          onWorkspaceChange={handleWorkspaceChange}
          onCreateTeam={() => {
            clearCreateTeamError();
            setIsCreateTeamDialogOpen(true);
          }}
          onJoinTeam={() => {
            clearJoinTeamError();
            setIsJoinTeamDialogOpen(true);
          }}
        />
      ) : null}

      <PromptsToolbar
        searchFilter={searchFilter}
        isCloudSource={isCloudSource}
        onSearchFilterChange={setSearchFilter}
        onCreateFolder={() => setFolderNameDialog({ mode: 'create' })}
      />

      {isOffline && isCloudSource ? (
        <p className="m-0 text-xs text-destructive">Offline: cloud prompt sync is unavailable.</p>
      ) : null}

      {statusMessage ? <p className="m-0 text-xs text-muted-foreground">{statusMessage}</p> : null}

      {error ? (
        <Card className="border-destructive/30">
          <p className="m-0 text-xs text-destructive">{error}</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleRetry}>
              Retry
            </Button>
          </div>
        </Card>
      ) : null}

      {isLoading ? <p className="m-0 text-xs text-muted-foreground">Loading prompts...</p> : null}

      {!isLoading && filteredPrompts.length === 0 ? (
        <p className="m-0 px-2 py-6 text-center text-[0.8125rem] text-muted-foreground">
          No prompts found. Create one to get started.
        </p>
      ) : null}

      <div className="flex flex-col gap-3">
        {sortedFolders.map((folder) => {
          const folderPrompts = promptsByFolder.get(folder.id) ?? [];
          return (
            <PromptFolderSection
              key={folder.id}
              title={`${folder.name} (${folderPrompts.length})`}
              prompts={folderPrompts}
              emptyMessage="No prompts in this folder."
              onInsertPrompt={handleInsertPrompt}
              onEditPrompt={(prompt) => setEditorState(createPromptEditorState(prompt))}
              onDeletePrompt={(prompt) => setPromptPendingDelete(prompt)}
              onRenameFolder={() => setFolderNameDialog({ mode: 'rename', folder })}
              onDeleteFolder={() => setFolderPendingDelete(folder)}
            />
          );
        })}

        <PromptFolderSection
          title={`Unfiled (${unfiledPrompts.length})`}
          prompts={unfiledPrompts}
          emptyMessage="No unfiled prompts."
          onInsertPrompt={handleInsertPrompt}
          onEditPrompt={(prompt) => setEditorState(createPromptEditorState(prompt))}
          onDeletePrompt={(prompt) => setPromptPendingDelete(prompt)}
        />
      </div>

      {editorState ? (
        <PromptEditorDialog
          state={editorState}
          isCloudSource={isCloudSource}
          folders={sortedFolders}
          onClose={() => setEditorState(null)}
          onSave={handleSavePrompt}
        />
      ) : null}

      {templateFillState ? (
        <TemplateFillDialog
          state={templateFillState}
          onClose={() => setTemplateFillState(null)}
          onInsert={handleResolvedInsert}
        />
      ) : null}

      <TextInputDialog
        open={folderNameDialog !== null}
        title={folderNameDialog?.mode === 'rename' ? 'Rename Folder' : 'Create Folder'}
        label="Folder name"
        initialValue={folderNameDialog?.mode === 'rename' ? folderNameDialog.folder.name : ''}
        placeholder="e.g. Marketing"
        confirmLabel={folderNameDialog?.mode === 'rename' ? 'Rename' : 'Create'}
        onCancel={() => setFolderNameDialog(null)}
        onConfirm={handleConfirmFolderName}
      />

      <TextInputDialog
        open={isCreateTeamDialogOpen}
        title="Create Team"
        label="Team name"
        description="Create a shared workspace for team prompts and folders."
        helperText={`Max ${MAX_TEAM_NAME_LENGTH} characters.`}
        placeholder="e.g. Growth"
        confirmLabel="Create"
        maxLength={MAX_TEAM_NAME_LENGTH}
        errorMessage={createTeamError}
        isPending={isCreatingTeam}
        onCancel={() => {
          clearCreateTeamError();
          setIsCreateTeamDialogOpen(false);
        }}
        onConfirm={handleCreateTeam}
      />

      <JoinTeamDialog
        open={isJoinTeamDialogOpen}
        isLookingUp={isLookingUpTeamInvite}
        isJoining={isJoiningTeam}
        errorMessage={joinTeamError}
        onCancel={() => {
          clearJoinTeamError();
          setIsJoinTeamDialogOpen(false);
        }}
        onLookupInvite={lookupTeamInvite}
        onConfirmJoin={handleJoinTeam}
      />

      <ConfirmDialog
        open={promptPendingDelete !== null}
        title="Delete Prompt"
        description={promptPendingDelete ? `Delete prompt "${promptPendingDelete.title}"?` : ''}
        confirmLabel="Delete"
        destructive
        onCancel={() => setPromptPendingDelete(null)}
        onConfirm={handleConfirmPromptDelete}
      />

      <ConfirmDialog
        open={folderPendingDelete !== null}
        title="Delete Folder"
        description={folderDeleteDescription}
        confirmLabel="Delete"
        destructive
        onCancel={() => setFolderPendingDelete(null)}
        onConfirm={handleConfirmFolderDelete}
      />

      </div>

      <Button
        onClick={() => setEditorState(createPromptEditorState())}
        aria-label="Create new prompt"
        className="absolute bottom-5 right-5 h-14 w-14 rounded-full shadow-lg"
      >
        <Plus className="!h-7 !w-7" />
      </Button>
    </div>
  );
}
